/**
 * useDocumentSemanticSearch — the Ablage-page Volltext/Semantisch toggle (ea/fix-search).
 *
 * Mirrors `usePolicySemanticSearch` (apps/web/src/features/policies/hooks) exactly, scoped
 * to documents instead of policies:
 *
 * live mode → GET /search?scope=document via fetchDocumentSemanticSearch.
 * mock mode → mockDocumentSearch(q) (deterministic, zero network).
 *
 * Unlike the Policies-page toggle (where Volltext ALSO fires this same hook — WEB-1 fix,
 * wave dms-fixC — because the old client-side substring filter could never reach PDF
 * content), the Ablage grid's Volltext mode already runs a real server-side fulltext leg
 * (`useDocumentRows`' `q` param → GET /documents?q=, filename + extracted_text_tsv). So
 * ONLY Semantisch uses this hook — Volltext keeps its existing (already-correct) grid
 * behaviour untouched.
 *
 * [Source: apps/web/src/features/policies/hooks/usePolicySemanticSearch.ts]
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SearchResponseDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchDocumentSemanticSearch, mockDocumentSearch } from '../../search/api/search-api';

/** Minimum query length before a semantic search fires (mirrors the Policies-page constant). */
export const DOCUMENT_SEMANTIC_MIN_QUERY_LENGTH = 2;

/** Debounce window (ms) — the Policies-page precedent. */
export const DOCUMENT_SEMANTIC_DEBOUNCE_MS = 250;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Run the debounced document-scoped semantic search (the Ablage page's "Semantisch" mode). */
export function useDocumentSemanticSearch(rawQuery: string, enabled: boolean) {
  const debounced = useDebounced(rawQuery.trim(), DOCUMENT_SEMANTIC_DEBOUNCE_MS);
  const live = isLiveData();
  const active = enabled && debounced.length >= DOCUMENT_SEMANTIC_MIN_QUERY_LENGTH;

  const query = useQuery<SearchResponseDto>({
    queryKey: qk.documentSemanticSearch(debounced),
    queryFn: () =>
      live ? fetchDocumentSemanticSearch(debounced) : Promise.resolve(mockDocumentSearch(debounced)),
    enabled: active,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  return {
    hits: active ? (query.data?.items ?? []) : [],
    isLoading: active && query.isLoading,
    isError: active && query.isError,
    searched: active,
    // S4: the semantic leg degrades silently when the embed service is down - the
    // response says so and the screen must be able to say it too. Only false once a
    // response actually carried semantic_available: false; an absent field (mock mode,
    // older api) is not a degradation.
    semanticAvailable: query.data?.semantic_available !== false,
  };
}
